<template>
  <div class="componentRoot" v-if="userData">
    <v-progress-linear
      v-if="refreshing && !user"
      indeterminate
      color="primary"
    />
    <v-dialog v-model="subscriptionCancelDialog" max-width="520" :persistent="cancellingSubscriptionID !== null">
      <v-card>
        <v-card-title>{{ $t('messages.cancelSubscriptionTitle') }}</v-card-title>
        <v-card-text>
          <p>{{ selectedSubscription?.name }}</p> 
          <p>{{ $t('messages.cancelSubscriptionDescription', { date: getReadableDate(selectedSubscription?.access_until) }) }}</p>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" :disabled="cancellingSubscriptionID !== null" @click="subscriptionCancelDialog = false">{{ $t('basics.back') }}</v-btn>
          <v-spacer />
          <v-btn color="error" variant="text" :loading="cancellingSubscriptionID !== null"
            :disabled="cancellingSubscriptionID !== null" @click="cancelSubscription">{{ $t('basics.confirmCancellation') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-alert v-if="subscriptionCancellationMessage" type="success" style="    flex-grow: 0 !important;
    min-height: 64px !important;" variant="tonal" class="mb-4" role="status">
      {{ subscriptionCancellationMessage }}
    </v-alert>

    <template v-if="user">
      <v-card class="mb-4">
        <v-card-title>
          {{ $t('basics.digitalContents') }}
        </v-card-title>

        <v-card-text>
          <v-table v-if="user.digital_medias.length">
            <thead>
              <tr>
                <th>{{ $t('basics.content') }}</th>
                <th>{{ $t('basics.paymentType') }}</th>
                <th>{{ $t('basics.price') }}</th>
                <th>{{ $t('basics.access') }}</th>
                <th>{{ $t('basics.paidUntil') }}</th>
                <th>{{ $t('basics.actions') }}</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="item in user.digital_medias" :key="item.id">
                <td>{{ item.name }}</td>

                <td>
                  <v-chip size="small">
                    {{
                      $t(
                        item.is_subscription
                          ? 'basics.monthlySubscription'
                          : 'basics.oneTimePayment'
                      )
                    }}
                  </v-chip>
                </td>

                <td>
                  {{ formatPrice(item.price) }}
                  {{ item.is_subscription ? ' / ' + $t('basics.month') : '' }}
                </td>

                <td>
                  <v-chip
                    size="small"
                    :color="item.has_access ? 'success' : undefined"
                  >
                    {{
                      $t(
                        item.has_access
                          ? 'basics.active'
                          : 'basics.endedOrBlocked'
                      )
                    }}
                  </v-chip>

                  <small v-if="item.cancel_at_period_end" class="d-block">
                    {{ $t('messages.cancelledAtPeriodEnd') }}
                  </small>
                </td>

                <td>
                  {{
                    item.is_subscription
                      ? getReadableDate(item.access_until)
                      : $t('basics.permanent')
                  }}
                </td>
                <td>
                  <template v-if="item.is_subscription">
                    <small v-if="item.cancel_at_period_end" class="d-block">
                      {{ $t('messages.subscriptionCancelledUntil', { date: getReadableDate(item.access_until) }) }}
                    </small>
                    <template v-else-if="item.can_cancel_subscription">
                      <v-btn size="small" variant="text" color="error"
                        :disabled="refreshing || cancellingSubscriptionID !== null"
                        @click="selectSubscriptionCancellation(item)">
                        {{ $t('basics.cancelAtPeriodEnd') }}
                      </v-btn>
                      <small v-if="item.cancellation_deadline" class="d-block">
                        {{ $t('messages.subscriptionCancellationDeadline', { date: getReadableDate(item.cancellation_deadline) }) }}
                      </small>
                    </template>
                    <small v-else-if="['active', 'past_due'].includes(item.stripe_subscription_status)" class="d-block">
                      {{ $t('messages.subscriptionCancellationUnavailable') }}
                    </small>
                  </template>
                </td>
              </tr>
            </tbody>
          </v-table>

          <i v-else>{{ $t('messages.noItems') }}</i>
        </v-card-text>
      </v-card>

      <v-card class="mb-4">
        <v-card-title>
          {{ $t('basics.bookedAppointments') }}
        </v-card-title>

        <v-card-text>
          <v-table v-if="user.appointments.length">
            <thead>
              <tr>
                <th>{{ $t('basics.appointment') }}</th>
                <th>{{ $t('basics.date') }}</th>
                <th>{{ $t('basics.time') }}</th>
                <th>{{ $t('basics.price') }}</th>
                <th>{{ $t('basics.payment') }}</th>
                <th>{{ $t('basics.invoice') }}</th>
                <th>{{ $t('basics.actions') }}</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="item in user.appointments" :key="item.id">
                <td>
                  {{ item.name }}

                  <small v-if="item.is_follow_up" class="d-block">
                    {{ $t('basics.followUpAppointment') }}
                  </small>
                </td>

                <td>{{ getReadableDate(item.date) }}</td>

                <td>
                  {{ item.start_time || '–' }} – {{ item.end_time || '–' }}
                </td>

                <td>{{ formatPrice(item.price) }}</td>

                <td>
                  <v-chip
                    size="small"
                    :color="
                      item.is_paid
                        ? 'success'
                        : item.checkout_reserved
                          ? 'warning'
                          : undefined
                    "
                  >
                    {{
                      $t(
                        item.is_paid
                          ? 'basics.paid'
                          : item.checkout_reserved
                            ? 'messages.reservedInCheckout'
                            : 'basics.pending'
                      )
                    }}
                  </v-chip>
                </td>

                <td>{{ item.bill_id ? '#' + item.bill_id : '–' }}</td>

                <td>
                  <v-btn
                    v-if="canCancelAppointment(item)"
                    size="small"
                    variant="text"
                    color="error"
                    :loading="cancellingCheckoutID === item.id"
                    :disabled="cancellingCheckoutID !== null"
                    @click="cancelCheckout(item)"
                  >
                    {{ $t('basics.cancelAppointment') }}
                  </v-btn>
                  <small v-else-if="requiresPhoneCancellation(item)" class="d-block">
                    {{ $t('messages.appointmentCancellationByPhone') }}
                  </small>
                </td>
              </tr>
            </tbody>
          </v-table>

          <i v-else>{{ $t('messages.noItems') }}</i>
        </v-card-text>
      </v-card>

      <v-card @click.stop>
        <v-card-title>
          {{ $t('basics.invoices') }}
        </v-card-title>

        <v-card-text>
          <v-alert v-if="invoiceEmailMessage" type="success" variant="tonal" class="mb-4" role="status">
            {{ invoiceEmailMessage }}
          </v-alert>
          <v-table v-if="user.bills.length">
            <thead>
              <tr>
                <th>{{ $t('basics.invoice') }}</th>
                <th>{{ $t('basics.invoiceDateOrDueDate') }}</th>
                <th>{{ $t('basics.amount') }}</th>
                <th>{{ $t('basics.status') }}</th>
                <th>{{ $t('basics.file') }}</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="bill in user.bills" :key="`${bill.invoice_type || 'regular'}-${bill.id}`">
                <td>
                  {{ bill.billing_number || '#' + bill.id }}
                  <small v-if="bill.invoice_type === 'shop'" class="d-block">
                    {{ $t(bill.invoice_kind === 'subscription' ? 'basics.monthlySubscription' : 'basics.oneTimePurchase') }}
                    <span v-if="bill.is_test"> · {{ $t('basics.testInvoice') }}</span>
                  </small>
                </td>
                <td>{{ getReadableDate(bill.due_date) }}</td>
                <td>{{ formatPrice(bill.total_sum) }}</td>
                <td>
                  {{ $t('constants.billing_status_' + bill.display_status) }}
                </td>

                <td>
                  <v-btn
                    v-if="bill.has_file"
                    size="small"
                    variant="text"
                    :loading="invoiceRequestKey === `${bill.invoice_type || 'regular'}-${bill.id}`"
                    :disabled="invoiceRequestKey !== null"
                    :title="$t(isNativeApp ? 'basics.sendInvoiceByEmail' : 'basics.downloadInvoice')"
                    @click="getBillFile($event, bill)"
                  >
                    <v-icon size="large">{{ isNativeApp ? 'mdi-email-outline' : 'mdi-download' }}</v-icon>
                  </v-btn>

                  <v-btn
                    v-if="bill.has_file && !isNativeApp"
                    size="small"
                    variant="text"
                    :disabled="invoiceRequestKey !== null"
                    :title="$t('basics.openInvoiceInNewTab')"
                    @click="getBillFile($event, bill, true)"
                  >
                    <v-icon size="large">mdi-open-in-new</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>

          <i v-else>{{ $t('messages.noItems') }}</i>
        </v-card-text>
      </v-card>
    </template>
  </div>
</template>

<script>
import { Capacitor } from '@capacitor/core';

export default {
  name: 'userProductDetail',

  props: ['userData'],

  data() {
    return {
      user: null,
      refreshing: false,
      loadFailed: false,
      requestSequence: 0,
      dataRequestSequence: 0,
      cancellingCheckoutID: null,
      cancellingSubscriptionID: null,
      subscriptionCancelDialog: false,
      selectedSubscription: null,
      selectedSubscriptionUserID: null,
      subscriptionCancellationMessage: '',
      invoiceRequestKey: null,
      invoiceEmailMessage: '', 
    };
  },

  computed: {
    isNativeApp() { return Capacitor.isNativePlatform(); },
    userID() {
      return this.userData?.id ?? this.$route.params.userID;
    },
  },

  watch: {
    userID: {
      immediate: true,
      handler() {
        this.subscriptionCancelDialog = false;
        this.selectedSubscription = null;
        this.subscriptionCancellationMessage = '';
        this.invoiceEmailMessage = '';
        this.requestSequence++;
        this.fetchData({ reset: true });
      },
    },
  },

  mounted() {
    window.addEventListener('focus', this.refreshPaymentStatus);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  },

  beforeUnmount() {
    window.removeEventListener('focus', this.refreshPaymentStatus);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.requestSequence++;
    this.releaseBillFiles();
  },

  methods: {
    selectSubscriptionCancellation(item) {
      if (!item.can_cancel_subscription || item.cancel_at_period_end || this.cancellingSubscriptionID !== null) return;
      this.selectedSubscription = item;
      this.selectedSubscriptionUserID = this.userID;
      this.subscriptionCancellationMessage = '';
      this.subscriptionCancelDialog = true;
    },

    async cancelSubscription() {
      const item = this.selectedSubscription;
      const userID = this.selectedSubscriptionUserID;
      if (!item || this.userID !== userID || this.cancellingSubscriptionID !== null) return;
      this.cancellingSubscriptionID = item.id;
      try {
        const response = await this.axios.post(
          this.axios.defaults.baseURL + 'user/users/products/' + userID + '/cancel-subscription/',
          { payment_profile_id: item.id }
        );
        if (response.data.cancel_at_period_end !== true) throw new Error(this.$t('messages.subscriptionCancellationNotConfirmed'));
        if (this.userID === userID) {
          this.subscriptionCancelDialog = false;
          this.subscriptionCancellationMessage = this.$t('messages.subscriptionCancellationConfirmed', { date: this.getReadableDate(response.data.access_until) });
          await this.fetchData();
        }
      } catch (error) {
        window.dispatchEvent(new CustomEvent('showErrorDisplay', { detail: error }));
        if (this.userID === userID) {
          this.subscriptionCancelDialog = false;
          await this.fetchData();
        }
      } finally {
        this.cancellingSubscriptionID = null;
      }
    },

    requiresPhoneCancellation(item) {
      return item.is_paid === true || Boolean(item.bill_id);
    },

    canCancelAppointment(item) {
      return item.is_paid === false &&
        !item.is_follow_up &&
        !this.requiresPhoneCancellation(item) &&
        item.can_cancel !== false;
    },

    refreshPaymentStatus() {
      if (!this.refreshing && this.cancellingCheckoutID === null && this.cancellingSubscriptionID === null &&
          this.invoiceRequestKey === null && !this.subscriptionCancelDialog) {
        this.fetchData();
      }
    },

    onVisibilityChange() {
      if (document.visibilityState === 'visible') this.refreshPaymentStatus();
    },
    async fetchData({ reset = false } = {}) {
      const sequence = ++this.dataRequestSequence;
      const generation = this.requestSequence;
      const userID = this.userID;
      if (reset || !userID) {
        this.releaseBillFiles();
        this.user = null;
      }
      this.loadFailed = false;
      if (!userID) {
        this.refreshing = false;
        return;
      }
      this.refreshing = true;
      try {
        const response = await this.axios.get(
          this.axios.defaults.baseURL + 'user/users/products/' + userID
        );
        if (sequence !== this.dataRequestSequence || generation !== this.requestSequence || userID !== this.userID) return;
        const previousBills = new Map((this.user?.bills || []).map(bill => [
          `${bill.invoice_type || 'regular'}-${bill.id}`, bill,
        ]));
        const bills = (response.data.bills || []).map(bill => {
          const key = `${bill.invoice_type || 'regular'}-${bill.id}`;
          const existing = previousBills.get(key);
          previousBills.delete(key);
          if (!existing) return bill;
          if (!bill.has_file && existing.blob) {
            URL.revokeObjectURL(existing.blob);
            delete existing.blob;
          }
          // Preserve existing row objects and Blob URLs during focus refreshes.
          return Object.assign(existing, bill);
        });
        for (const bill of previousBills.values()) {
          if (bill.blob) URL.revokeObjectURL(bill.blob);
        }
        this.user = {
          ...response.data,
          digital_medias: response.data.digital_medias || [],
          appointments: response.data.appointments || [],
          bills,
        };
      } catch (error) {
        if (sequence !== this.dataRequestSequence || generation !== this.requestSequence) return;
        this.loadFailed = true;
        window.dispatchEvent(new CustomEvent('showErrorDisplay', { detail: error }));
      } finally {
        if (sequence === this.dataRequestSequence && generation === this.requestSequence) this.refreshing = false;
      }
    },

    async cancelCheckout(item) {
      if (this.cancellingCheckoutID !== null || !this.canCancelAppointment(item)) return;

      const userID = this.userID;
      this.cancellingCheckoutID = item.id;

      try {
        // Vor dem Abbruch den aktuellen Zahlungsstatus laden.
        await this.fetchData();
        if (this.userID !== userID || this.loadFailed || !this.user) return;
        const currentItem = this.user.appointments.find(entry => entry.id === item.id);
        if (!currentItem || !this.canCancelAppointment(currentItem)) return;
        await this.axios.post(
          this.axios.defaults.baseURL +
            'user/users/products/' +
            userID +
            '/cancel-checkout/',
          {
            appointment_id: item.id,
          }
        );

        if (this.userID === userID) {
          await this.fetchData();
        }
      } catch (error) {
        if (error.response?.status === 409 && this.userID === userID) {
          await this.fetchData();
          // Konflikte können auch einen anderen Grund als eine Zahlung haben.
          const currentItem = this.user?.appointments.find(entry => entry.id === item.id);
          if (currentItem) currentItem.can_cancel = false;
        }
        window.dispatchEvent(
          new CustomEvent('showErrorDisplay', { detail: error })
        );
      } finally {
        this.cancellingCheckoutID = null;
      }
    },

    getReadableDate(date) {
      return date ? this.$moment(date).format('DD.MM.YYYY') : '–';
    },

    formatPrice(price) {
      if (
        price == null ||
        price === '' ||
        !Number.isFinite(Number(price))
      ) {
        return '–';
      }

      return new Intl.NumberFormat(this.$i18n.locale || 'de-DE', {
        style: 'currency',
        currency: 'EUR',
      }).format(Number(price));
    },

    async getBillFile(event, bill, newTab = false) {
      event.stopPropagation();
      event.preventDefault();
      if (this.invoiceRequestKey !== null) return;
      const native = Capacitor.isNativePlatform();
      // A native client must never reuse/open an earlier browser Blob.
      if (!native && bill.blob) {
        this.openFile(bill, newTab);
        return;
      }
      const sequence = this.requestSequence;
      this.invoiceRequestKey = `${bill.invoice_type || 'regular'}-${bill.id}`;
      this.invoiceEmailMessage = '';
      try {
        const response = await this.axios({
          method: 'POST',
          url: this.axios.defaults.baseURL + (bill.invoice_type === 'shop'
            ? 'user/shop-invoices/file/profile/' : 'user/bills/file/profile/'),
          responseType: native ? 'json' : 'blob',
          data: { id: bill.id, use_capacitor: native },
        });
        if (sequence !== this.requestSequence) return;
        if (native) {
          if (response.data?.mail_sent !== true) throw new Error(this.$t('messages.invoiceEmailNotConfirmed'));
          this.invoiceEmailMessage = this.$t('messages.invoiceEmailSent');
          return;
        }
        const contentDisposition = response.headers['content-disposition'] || '';
        const filenameMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
        bill.filename = filenameMatch ? filenameMatch[1].trim() : this.$t('messages.invoiceFilename', { id: bill.id }) + '.pdf';
        bill.blob = URL.createObjectURL(response.data);
        this.openFile(bill, newTab);
      } catch (error) {
        if (sequence !== this.requestSequence) return;
        window.dispatchEvent(new CustomEvent('showErrorDisplay', { detail: error }));
      } finally {
        this.invoiceRequestKey = null;
      }
    },

    openFile(bill, newTab) {
      if (newTab) {
        window.open(bill.blob, '_blank', 'noopener,noreferrer');
        return;
      }

      const link = document.createElement('a');
      link.href = bill.blob;
      link.download = bill.filename || this.$t('messages.invoiceFilename', { id: bill.id }) + '.pdf';

      document.body.appendChild(link);
      link.click();
      link.remove();
    },

    releaseBillFiles() {
      for (const bill of this.user?.bills || []) {
        if (bill.blob) {
          URL.revokeObjectURL(bill.blob);
          delete bill.blob;
        }
      }
    },
  },
};
</script>